import { Card, withStyles } from 'vopay';
import { useHistory } from 'react-router-dom';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import DialogLink from '../DialogLink';
import { Button, CircleButton } from 'UI';

import {
  useElinxRequestNameRows,
  useElinxRequestMethodRows,
  useElinxRequestDetailRows,
  FormValues,
  SerializedFormValues,
  buildInitialValues,
} from './buildFormConfig';

import styles from './ElinxRequestDialog.module.scss';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { Title } from 'UI';
import { useForm } from '@/components/Form/utils';
import { useDataFetchIndicator } from '@/hooks';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type ElinxRequestProps = {
  accountName: string;
  accountID: string;
  email: string;
  onClose: () => void;
};

const ElinxRequestDialogue = ({ accountName, accountID, email, onClose }: ElinxRequestProps) => {
  const [, setPending] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const { response } = useClientData();
  const { setSubNavActiveModule } = useNavMenu();
  const [, , , generateEmbedUrl] = usePostEmbedUrl({});
  const [, , , sendPaylink] = usePostSendPaylink({});
  const initialValues = buildInitialValues(accountName, response.AccountName);
  const sendElinxNameRows = useElinxRequestNameRows();
  const sendElinxMethodRows = useElinxRequestMethodRows();
  const sendElinxDetailRows = useElinxRequestDetailRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.ElinxRequestDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { renderFormElements, ctx } = useForm<FormValues, SerializedFormValues>({
    initialValues: initialValues,
    config: [...sendElinxNameRows, ...sendElinxMethodRows, ...sendElinxDetailRows],
    onSubmit: async (values) => {
      try {
        const sendPaylinkRes = await sendRequest(values);

        const successChoice = await alertDialog.success(
          t('Success.title', { defaultValue: 'eLinx Connect Request successfully sent!' }),
          <div className={cn('sendRequestSuccessDetails')}>
            {t('Success.content', {
              defaultValue: `The connection request will be sent to ${accountName} to connect their Payment Method. Once the request is complete you can start processing EFT transactions with this client account.`,
              accountName,
            })}
            <DialogLink
              label={t('Success.dialogLink', { defaultValue: 'eLinx Embedded Link' })}
              link={sendPaylinkRes.Link}
            />
          </div>,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
            confirmLabel: t('Success.confirmLabel', { defaultValue: 'View Request Details' }),
          },
        );

        if (successChoice === 'confirm') {
          history.push({
            pathname: `/elinx-history/details/${sendPaylinkRes.ELinxRequestID}`,
            state: {
              eLinx: sendPaylinkRes,
            },
          });

          setSubNavActiveModule('paylinkHistory');
        } else {
          ctx.resetForm();
        }

        onClose();
      } catch (err) {
        if (err instanceof Error) {
          alertDialog.error(
            t('Error.title', { defaultValue: 'Error! The eLinx Connect Request was not sent.' }),
            err.message,
            {
              abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
            },
          );
        }
      }
    },
  });

  async function sendRequest(values: SerializedFormValues) {
    try {
      setPending(true);

      const embedUrlRes = await generateEmbedUrl({
        RedirectURL: 'https://request-dev.vopay.com/',
        Language: 'en',
        RedirectMethod: 'JavascriptMessage',
        PaymentSelectionMethod: values.PaymentSelectionMethod,
        AccountSelectionMethod: values.AccountSelectionMethod,
        ClientControlled: response.clientAccountsEnabled,
        ClientReferenceNumber: '',
        TermsAndConditions: '',
        Country: response.Country,
        ClientAccountID: accountID,
      });

      if (!embedUrlRes.Success) {
        throw new Error(embedUrlRes.ErrorMessage);
      }

      const sendPaylinkRes = await sendPaylink({
        SenderName: values.SenderName,
        ReceiverName: values.ReceiverName,
        ReceiverEmailAddress: email,
        IframeKey: embedUrlRes.IframeKey,
        InvoiceNumber: values.InvoiceNumber,
        Note: values.Note,
        RecipientMessage: values.Note,
        ClientAccountID: accountID,
        SendNotification: values.SendEmail,
        NotificationMethod: 'email',
      });

      if (!sendPaylinkRes.Success) {
        throw new Error(sendPaylinkRes.ErrorMessage);
      }

      return sendPaylinkRes;
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className={cn('elinxRequestContainer')}>
      <CircleButton icon="close" onClick={onClose} className={cn('closeButton')} circleFill={false} size="large" />

      <form onSubmit={ctx.handleSubmit} className={cn('form')}>
        <Title level="4">{t('title', { defaultValue: 'Send eLinx Request' })}</Title>
        <div className={cn('row')}>{renderFormElements({ config: sendElinxNameRows })}</div>
        <Title level="5">{t('details', { defaultValue: 'eLinx Details' })}</Title>
        <div className={cn('row')}>{renderFormElements({ config: sendElinxMethodRows })}</div>
        {renderFormElements({ config: sendElinxDetailRows })}

        <Button
          colorScheme="primary"
          type="submit"
          disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
          style={{
            marginLeft: 'auto',
          }}
        >
          {t('submitButton', { defaultValue: 'Send eLinx Request' })}
        </Button>
      </form>
    </Card>
  );
};

export default ElinxRequestDialogue;
